import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { getNewIDCall } from "../redux/slice/upload-bills/getNewId";
import { uploadBillsCall } from "../redux/slice/upload-bills/uploadBill";

import uparrow from "../assets/images/uparrow.svg";
import Spinner from "./spinner/Spinner";
import { toast } from "react-toastify";

const MyVerticallyCenteredModal2 = forwardRef((props, ref) => {
  const mobile = localStorage.getItem("mobile");

  const dispatch = useDispatch();
  const [imageObject, setImageObject] = useState({
    uploadedFile: null,
    previewMode: false,
    fileUrl: null,
    base64: "",
    extension: "",
    error: false,
  });
  const [bills, setBills] = useState([]);
  const [loading2, setLoading2] = useState(false);
  const [selectedBillIndex, setSelectedBillIndex] = useState(null);

  const { uploadBillsData, loading, success } = useSelector(
    (state) => state.uploadBills
  );
  const maxSize = 5 * 1024 * 1024;
  const maxBills = 4;
  const billCnt = bills.length;
  // A picked file that was never confirmed with Next still counts as a bill
  const pendingBill =
    imageObject?.base64 && !imageObject?.error ? imageObject.base64 : null;

  const handleFileChange = (e) => {
    
    const file = e.target.files[0];
    if (file) {

      setImageObject((pre) => {
        let obj = {
          ...pre,
          extension: file?.type?.split("/")[1],
          uploadedFile: file,
          fileUrl: URL.createObjectURL(file),
          previewMode: false,
        };

        return obj;
      });

      if (file?.size > maxSize) {
        setImageObject((pre) => {
          return { ...pre, error: true };
        });
      } else {
        setImageObject((pre) => {
          return { ...pre, error: false };
        });
      }
      // converting image to Base 64 logic
      const reader = new FileReader();
      reader.readAsDataURL(file); // Convert to Base64

      reader.onload = () => {
        // setBase64(reader.result); // Set the Base64 string

        setImageObject((pre) => {
          let obj = {
            ...pre,
            base64: reader.result,
          };
          return obj;
        });
      };

      reader.onerror = (error) => {
        console.error("Error converting to Base64:", error);
      };
    }
  };

  const handlePreview = () => {
    if (imageObject?.uploadedFile) {
      setImageObject((pre) => {
        let obj = {
          ...pre,
          previewMode: !pre.previewMode,
        };

        return obj;
      });
    }
  };

  const handleReset = () => {
    setImageObject({
      uploadedFile: null,
      previewMode: false,
      fileUrl: null,
      base64: "",
      extension: "",
      error: false,
    });
    
    // Reset the file input element
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  useImperativeHandle(ref, () => ({
    handleReset,
  }));
  const handleSubmitBill = () => {
    if (billCnt >= maxBills) {
      toast.error(`Maximum ${maxBills} bills can be uploaded`);
      return;
    }
    if (!pendingBill) {
      toast.error("Please wait for the image to finish loading");
      return;
    }

    setBills((prev) =>
      prev.length >= maxBills ? prev : [...prev, { src: pendingBill }],
    );
    setSelectedBillIndex(null);
    handleReset();
  };

  const handleSubmitAll = async () => {
    const originalId = sessionStorage.getItem("redirectId");
    const submittedId = sessionStorage.getItem("submittedRedirectId");

    if (!originalId) {
      toast.error("Missing request ID");
      return;
    }
    if (originalId === submittedId) {
      toast.error("Bills have already been submitted for this request");
      return;
    }

    const queuedBills = [...bills.map((bill) => bill?.src), pendingBill]
      .filter(Boolean)
      .slice(0, maxBills);

    if (queuedBills.length === 0) {
      toast.error("Please upload at least one bill");
      return;
    }

    setLoading2(true);
    try {
      const rejectResult = await dispatch(
        getNewIDCall({ requestId: originalId }),
      );
      const newRequestId = rejectResult?.payload?.data?.newRequestId;
      if (!newRequestId) {
        toast.error(
          rejectResult?.payload?.message || "Failed to generate new ID",
        );
        return;
      }

      for (const imageData of queuedBills) {
        const result = await dispatch(
          uploadBillsCall({
            requestId: newRequestId,
            submit: false,
            imageData,
          }),
        );
        if (result?.payload?.data?.uploadedImage?.status !== "true") {
          toast.error(
            result?.payload?.data?.uploadedImage?.message ||
              "Failed to upload bill",
          );
          return;
        }
      }

      const submitResult = await dispatch(
        uploadBillsCall({
          requestId: newRequestId,
          submit: true,
        }),
      );
      if (submitResult?.payload?.data?.updateResponse?.failureCount === 0) {
        toast.success(submitResult?.payload?.message);
        handleReset();
        setBills([]);
        setSelectedBillIndex(null);
        sessionStorage.setItem("submittedRedirectId", originalId);
        sessionStorage.removeItem("redirectId");
        props.onHide();
      } else {
        toast.error(
          submitResult?.payload?.data?.message || submitResult?.payload?.message,
        );
      }
    } finally {
      setLoading2(false);
    }
  };

  // useEffect(() => {
  //   return () => {
  //     // Cleanup code if needed
  //   };
  // }, []);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600); // Adjust breakpoint as needed
    };

    // Check screen size on component mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Clean up event listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const submitDisabled =
    (billCnt === 0 && !pendingBill) || loading || loading2;
  const nextDisabled = !pendingBill || billCnt >= maxBills || loading2;

  const handleBillPreview = (index) => {
    if (bills[index]?.src) {
      if (selectedBillIndex === index && imageObject.previewMode) {
        handleReset();
      } else {
        // Show preview of the clicked bill
        setImageObject(prev => ({
          ...prev,
          previewMode: true,
          fileUrl: bills[index].src
        }));
        setSelectedBillIndex(index);
      }
    }else{
      handleReset();
    }
  };

  return (
    <>
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered={!isMobile}
        className="conditional-modal slide-up-modal upload"
      >
        <button
          type="button"
          className="custom-close"
          aria-label="Close"
          onClick={() => {
            props.onHide();
            handleReset();
          }}
          style={{ top: "-10%", left: "-33px" }}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <Modal.Header>
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="text-center w-100"
          >
            {imageObject?.previewMode 
              ? `Bill Preview ${selectedBillIndex !== null ? `(${selectedBillIndex + 1}/${maxBills})` : ''}`
              : "Upload your Bills"
            }
            {imageObject?.uploadedFile && !selectedBillIndex && (
              <span className="preview" onClick={handlePreview}>
                {imageObject.previewMode ? "Hide Preview" : "Show Preview"}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-2">
          {
            <>
              {imageObject?.previewMode ? (
                <div>
                  <img
                    src={imageObject?.fileUrl}
                    alt="Uploaded file preview"
                    style={{ width: "100%", height: "auto" , maxHeight: "50vh"}}
                  />
                </div>
              ) : (
                <>
                  <div className="file-upload-box">
                    {/* Hidden file input */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      id="fileInput"
                      style={{ display: "none" }}
                    />
                    {/* Clickable image */}
                    <img
                      src={uparrow}
                      alt="Click to upload"
                      className="upload-image"
                      onClick={() =>
                        document.getElementById("fileInput").click()
                      }
                    />

                    {imageObject?.uploadedFile && (
                      <p>Selected File: {imageObject?.uploadedFile.name}</p>
                    )}
                    <p className="dragtext">Drag or click here to upload</p>
                  </div>
                  <div>
                    <p className="error-text">
                      {imageObject?.error
                        ? "File should be less than 5 mb"
                        : null}
                    </p>
                  </div>
                </>
              )}
            </>
          }
        </Modal.Body>

        <Modal.Footer>
          <div className="plus-cont">
            <div>
              {Array.from({ length: maxBills }).map((_, index) => {
                const bill = bills[index];
                return (
                  <div
                    key={index}
                    className="plus-icon"
                    onClick={() => handleBillPreview(index)}
                    style={{ cursor: bill?.src ? 'pointer' : 'default' }}
                  >
                    {bill?.src ? (
                      <img
                        src={bill.src}
                        alt={`bill preview ${index + 1}`}
                        style={{
                          width: "30px",
                          height: "30px",
                          objectFit: "cover"
                        }}
                      />
                    ) : "+"}
                  </div>
                );
              })}
            </div>
            <div>
              <p>Can upload upto {maxBills} bills!</p>
            </div>
          </div>
          <Button
            style={{ 
              display: "flex",
              backgroundColor: submitDisabled ? '#cccccc' : '#000',
              borderColor: submitDisabled ? '#cccccc' : '#000',
              cursor: submitDisabled ? 'not-allowed' : 'pointer'
            }}
            disabled={submitDisabled}
            onClick={handleSubmitAll}
          >
            Submit
            {loading2 && <Spinner />}
          </Button>
          <Button
            onClick={handleSubmitBill}
            disabled={nextDisabled}
            style={{ 
              display: "flex",
              backgroundColor: nextDisabled ? '#cccccc' : '#000',
              borderColor: nextDisabled ? '#cccccc' : '#000',
              cursor: nextDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default MyVerticallyCenteredModal2;
